/** The right-hand panel: what the selected node type is, and what hangs off it. */

import { Link } from 'react-router-dom'
import { CATEGORY_COLOR, Chip, FlagChip } from '../components'
import type { NodeTypeDef } from '../types/brain.types'

export function NodeInspector({
  graphId,
  node,
}: {
  graphId: string
  node: NodeTypeDef | undefined
}) {
  if (!node) {
    return (
      <div className="cp-insp">
        <div className="cp-sect">
          <p className="muted small" style={{ margin: 0 }}>
            Select a node type on the canvas to inspect it.
          </p>
        </div>
      </div>
    )
  }

  const colour = CATEGORY_COLOR[node.category] ?? 'var(--core)'
  const rules =
    (node.dq_rule_count ?? 0) + (node.match_rule_count ?? 0) + (node.survivorship_rule_count ?? 0)

  return (
    <div className="cp-insp">
      <div className="cp-ihead">
        <span
          className="cp-mark"
          style={{ background: `color-mix(in srgb, ${colour} 18%, transparent)`, color: colour }}
          aria-hidden="true"
        >
          {node.key.slice(0, 1).toUpperCase()}
        </span>
        <span className="nm truncate">{node.key}</span>
        <span style={{ marginLeft: 'auto' }}>
          <Chip tone={node.category}>{node.category}</Chip>
        </span>
      </div>

      <div className="cp-kpis">
        <div className="cp-kpi">
          <b>{(node.record_count ?? 0).toLocaleString()}</b>
          <span>records</span>
        </div>
        <div className="cp-kpi">
          <b>{node.property_count}</b>
          <span>props</span>
        </div>
        <div className="cp-kpi">
          <b>{node.edge_count}</b>
          <span>edges</span>
        </div>
      </div>

      {node.description && (
        <div className="cp-sect">
          <p className="small muted" style={{ margin: 0 }}>
            {node.description}
          </p>
        </div>
      )}

      <div className="cp-sect">
        <h4>Properties</h4>
        {node.properties.length === 0 && <p className="small faint">None defined yet.</p>}
        {node.properties.map((property) => (
          <div key={property.property_id} className="cp-prow">
            <span className="k truncate">{property.key}</span>
            <span className="ty">{property.data_type}</span>
            <span className="fl">
              {property.flags.map((flag) => (
                <FlagChip key={flag} flag={flag} />
              ))}
            </span>
          </div>
        ))}
      </div>

      <div className="cp-sect">
        <h4>Relationships</h4>
        {node.edges_out.length === 0 && node.edges_in.length === 0 && (
          <p className="small faint">None defined yet.</p>
        )}
        {node.edges_out.map((edge) => (
          <div key={`out-${edge.edge_type_id}`} className="cp-erow">
            <span className="lab truncate">→ {edge.key}</span>
            <span className="to truncate">{edge.to_node_type}</span>
          </div>
        ))}
        {node.edges_in.map((edge) => (
          <div key={`in-${edge.edge_type_id}`} className="cp-erow">
            <span className="lab truncate">← {edge.key}</span>
            <span className="to truncate">{edge.from_node_type}</span>
          </div>
        ))}
      </div>

      <div className="cp-sect">
        <h4>Rules</h4>
        <div className="cp-erow">
          <span className="lab">data quality</span>
          <span className="to">{node.dq_rule_count ?? 0}</span>
        </div>
        <div className="cp-erow">
          <span className="lab">matching</span>
          <span className="to">{node.match_rule_count ?? 0}</span>
        </div>
        <div className="cp-erow">
          <span className="lab">survivorship</span>
          <span className="to">{node.survivorship_rule_count ?? 0}</span>
        </div>
        {rules === 0 && (
          <p className="small faint" style={{ margin: '8px 0 0' }}>
            Nothing is validated, matched or reconciled for this type yet.
          </p>
        )}
      </div>

      <div className="cp-sect">
        <Link
          className="btn btn-sm btn-block"
          to={`/brain/${graphId}/nodes/${node.node_type_id}`}
        >
          Open {node.key} →
        </Link>
      </div>
    </div>
  )
}
